import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import AttachKnowledgeDialog from 'components/@home/@settings/Tabs/Agents/AttachKnowledgeDialog';
import { useAgentEdit } from 'components/@home/@settings/Tabs/Agents/AgentEditContext';
import CloseIcon from 'mdi-react/CloseIcon';
import { arrayOf, bool, func, object } from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import AgentSkill from 'services/api/AgentSkill';
import { I18n } from 'utils/i18n';

const ManageAgentKnowledgeDialog = ({
  open,
  onClose,
  knowledge = [],
  attachedSkills = [],
  onCreateKnowledge = null,
}) => {
  const { onReloadSkills } = useAgentEdit();
  const [selectedSkillId, setSelectedSkillId] = useState('');

  useEffect(() => {
    if (!open) {
      setSelectedSkillId('');
      return;
    }
    if (attachedSkills.length > 0) {
      setSelectedSkillId(prev => {
        if (prev && attachedSkills.some(s => String(s._id) === prev)) return prev;
        return String(attachedSkills[0]._id);
      });
    }
  }, [open, attachedSkills]);

  const selectedSkill = useMemo(
    () => attachedSkills.find(s => String(s._id) === String(selectedSkillId)),
    [attachedSkills, selectedSkillId],
  );

  const handleSave = async attachments => {
    if (!selectedSkill?._id) return;
    await AgentSkill.update(selectedSkill._id, { attachedKnowledge: attachments });
    await onReloadSkills?.();
  };

  if (attachedSkills.length === 0) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="xs"
        fullWidth
        slotProps={{ paper: { sx: { borderRadius: 2 } } }}
      >
        <DialogTitle
          component="div"
          sx={{
            fontSize: theme => theme.typography.pxToRem(18),
            fontWeight: 700,
            p: '20px 24px 12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {I18n.t('AgenticChatFlow.ManageKnowledge')}
          <IconButton size="small" onClick={onClose} aria-label={I18n.t('AgenticChatFlow.Cancel')}>
            <CloseIcon size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            p: '8px 24px 24px',
            '&.MuiDialogContent-root': { pt: 1 },
          }}
        >
          <Typography
            sx={{
              p: '32px 8px',
              textAlign: 'center',
              color: 'text.secondary',
              fontSize: theme => theme.typography.pxToRem(14),
              lineHeight: 1.5,
            }}
          >
            {I18n.t('AgenticChatFlow.NoAttachedSkills')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={onClose} color="inherit">
            {I18n.t('AgenticChatFlow.Cancel')}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <AttachKnowledgeDialog
      open={open}
      onClose={onClose}
      knowledge={knowledge}
      selectedAttachments={selectedSkill?.attachedKnowledge || []}
      onSave={handleSave}
      onCreateKnowledge={onCreateKnowledge}
      dialogTitle={I18n.t('AgenticChatFlow.ManageKnowledge')}
      skillOptions={attachedSkills.length > 1 ? attachedSkills : undefined}
      selectedSkillId={selectedSkillId}
      onSkillChange={setSelectedSkillId}
    />
  );
};

ManageAgentKnowledgeDialog.propTypes = {
  open: bool.isRequired,
  onClose: func.isRequired,
  knowledge: arrayOf(object),
  attachedSkills: arrayOf(object),
  onCreateKnowledge: func,
};

export default ManageAgentKnowledgeDialog;
